import React, { useState, useCallback, useRef } from 'react';
import { Box, Button, Typography, Modal, IconButton, LinearProgress, Divider, CircularProgress, GlobalStyles} from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import PDFICON from '../../assets/Images/PDF.svg';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from '../SnackbarContext';
import GlobalVariable from "../../services/Global";

const baseApiUrl = GlobalVariable.BASE_API_URL;
function buildFolderTree(fileMetas) {
  function insert(struct, folders, fileMeta) {
    if (!folders.length) {
      struct.files = struct.files || [];
      struct.files.push({
        file_name: fileMeta.filename,
        is_selected: fileMeta.is_selected === "true"
      });
      return;
    }
    const [head, ...tail] = folders;
    if (!struct.sub_folders) struct.sub_folders = [];
    let sub = struct.sub_folders.find(s => s.folder_name === head);
    if (!sub) {
      sub = { folder_name: head };
      struct.sub_folders.push(sub);
    }
    insert(sub, tail, fileMeta);
  }
  let root = fileMetas[0]?.folderStructure?.folders?.[0] || "Uploaded_Folder";
  const struct = { folder_name: root };
  fileMetas.forEach(meta =>
    insert(
      struct,
      meta.folderStructure.folders.slice(1),
      meta
    )
  );
  return struct;
}

const FileUploadModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfText, setPdfText] = useState('');
  const [isCloseDisabled, setIsCloseDisabled] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  const uploadTimer = useRef(null);

  const simulateFileUpload = useCallback((currentProgress = 0) => {
    uploadTimer.current = setInterval(() => {
      if (currentProgress < 90) {
        currentProgress += 5;
        setProgress(currentProgress);
      }
    }, 500);
  }, []);

  const uploadFile = async (fileObj) => {
    const storedData = JSON.parse(localStorage.getItem('userDetails'));
    const userid = storedData.userDetails.id;
    try {
      // For single file upload, use the same folder payload shape: allFiles + selectedFiles
      const folderPath = fileObj.webkitRelativePath ? fileObj.webkitRelativePath : fileObj.name;
      const pathParts = folderPath.split('/');
      const folderStructure = {
        fullPath: folderPath,
        folders: pathParts.slice(0, -1),
        filename: pathParts[pathParts.length - 1],
        depth: pathParts.length - 1
      };

      const base64File = await convertToBase64(fileObj);

      const fileData = {
        filename: fileObj.name,
        base64: base64File,
        folderPath: folderPath,
        folderStructure: folderStructure,
        fileSize: fileObj.size,
        fileType: fileObj.type,
        is_selected: "true",
        is_main_rfp: "true"
      };

      const requestData = {
        userid: userid,
        allFiles: [fileData],
        selectedFiles: [fileData]
      };


      const response = await axios.post(`${baseApiUrl}/dashboard/addrfpfileupload`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      setProgress(100);

      if (response.data.status_code === 200) {
        const result = response.data.result;
        setPdfText(result);

        showSnackbar(`${fileObj.name} uploaded successfully.`, "success");
        navigate('/dashboard/addNewRfp/ExtrctedPdfDocument', { state: { pdfText: result } });
        return result;
      }
    } catch (error) {
      console.error('Upload error:', error);
      const apiErrorMessage = error.response?.data?.message || error.response?.data?.error || null;
      if (error.response?.status === 500) {
        showSnackbar(apiErrorMessage || 'Server error: This File name not supported. Please change file name and upload it.', 'error');
        setFile(null);
      } else if (error.response?.status === 400) {
        showSnackbar(apiErrorMessage || 'Bad request: Please check your file name and format.', 'error');
        setFile(null);
      } else if (error.response?.status === 413) {
        showSnackbar(apiErrorMessage || 'File too large: Please upload a smaller file.', 'error');
        setFile(null);
      } else {
        showSnackbar(apiErrorMessage || 'Upload failed: Please try again.', 'error');
        setFile(null);
      }
      setProgress(0);
    } finally {
      setLoading(false);
      setUploading(false);
      clearInterval(uploadTimer.current);
      setIsCloseDisabled(false);
    }
  };

  const uploadMultipleFiles = async (files) => {
    const storedData = JSON.parse(localStorage.getItem('userDetails'));
    const userid = storedData.userDetails.id;
    
    try {
      // Check if this is a folder upload
      const isFromFolder = files.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));
      
      
      if (isFromFolder) {
  const allFilesData = files.map((file, index) => {
    const folderPath = file.webkitRelativePath ? file.webkitRelativePath : file.name;
    const pathParts = folderPath.split('/');
    const folderStructure = {
      fullPath: folderPath,
      folders: pathParts.slice(0, -1),
      filename: pathParts[pathParts.length - 1],
      depth: pathParts.length - 1
    };
    // Check if file is .xlsx or .csv - these should not be selectable and not uploaded
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const isNonSelectable = extension === '.xlsx' || extension === '.csv';
    
    return {
      filename: file.name,
      folderPath,
      folderStructure,
      fileSize: file.size,
      fileType: file.type,
      // .xlsx and .csv files are shown in structure but not uploaded (is_selected: false)
      is_selected: (selectedFiles.has(index) && !isNonSelectable) ? "true" : "false",
      fileObj: file
    };
  });

  const folderTree = buildFolderTree(allFilesData);
  const formData = new FormData();
  formData.append('user_id', userid);
  formData.append('folder_structure', JSON.stringify(folderTree));
  // Append all files including .xlsx and .csv (they are sent as binary data but with is_selected: false)
  allFilesData.forEach(f => {
    formData.append('all_files', f.fileObj, f.filename);
  });

  const response = await axios.post(`${baseApiUrl}/dashboard/upload_folder`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'accept': 'application/json'
    }
  });

  if (response.data.status_code === 200) {
    const result = response.data.result;
    setPdfText(result);
    showSnackbar(`${selectedFiles.size} files uploaded successfully.`, "success");
    navigate('/dashboard/addNewRfp/ExtrctedPdfDocument', { state: { pdfText: result } });
    return result;
  }
      } else {
        // For multiple file uploads (not from folder), use addrfpfileupload endpoint
        const filesData = await Promise.all(
          files.map(async (file) => ({
            filename: file.name,
            base64: await convertToBase64(file)
          }))
        );

        const requestData = {
          userid: userid,
          files: filesData  // Send array of files in old format
        };

        const response = await axios.post(`${baseApiUrl}/dashboard/addrfpfileupload`, requestData, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.data.status_code === 200) {
          const result = response.data.result;
          setPdfText(result);
          showSnackbar(`${files.length} files uploaded successfully.`, "success");
          navigate('/dashboard/addNewRfp/ExtrctedPdfDocument', { state: { pdfText: result } });
          return result;
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 500) {
        showSnackbar('Server error: File names not supported. Please change file names and upload again.', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Bad request: Please check your file names and formats.', 'error');
      } else if (error.response?.status === 413) {
        showSnackbar('Files too large: Please upload smaller files.', 'error');
      } else {
        showSnackbar('Upload failed: Please try again.', 'error');
      }
      setProgress(0);
    } finally {
      setLoading(false);
      setUploading(false);
      clearInterval(uploadTimer.current);
      setIsCloseDisabled(false);
    }
  };

  const resetState = () => {
    setFile(null);
    setFiles([]);
    setSelectedFiles(new Set()); // Clear selected files
    setProgress(0);
    setUploading(false);
    setLoading(false);
    setPdfText('');
    setCurrentFileIndex(0);
    onClose();
  };


  const toggleFileSelection = (fileIndex) => {
    // Disable file selection during upload
    if (loading || uploading) {
      return;
    }
    
    // Check if file is .xlsx or .csv - these should not be selectable
    const file = files[fileIndex];
    if (file) {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (extension === '.xlsx' || extension === '.csv') {
        showSnackbar('XLSX and CSV files cannot be selected for upload. They are shown for reference only.', 'info');
        return;
      }
    }
    
    const newSelectedFiles = new Set(selectedFiles);
    if (newSelectedFiles.has(fileIndex)) {
      // Deselecting - always allowed
      newSelectedFiles.delete(fileIndex);
      setSelectedFiles(newSelectedFiles);
    } else {
      // Selecting - check if limit reached
      if (selectedFiles.size >= 3) {
        showSnackbar('Maximum 3 files can be selected. Please deselect a file first.', 'error');
        return;
      }
      newSelectedFiles.add(fileIndex);
      setSelectedFiles(newSelectedFiles);
    }
  };

  const selectAllFiles = () => {
    // Filter out .xlsx and .csv files from selection
    const selectableIndices = files
      .map((file, index) => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return (extension !== '.xlsx' && extension !== '.csv') ? index : null;
      })
      .filter(index => index !== null);
    
    if (selectableIndices.length > 3) {
      showSnackbar('Maximum 3 files can be selected. Please select files individually.', 'error');
      return;
    }
    if (selectableIndices.length === 1) {
      setSelectedFiles(new Set([selectableIndices[0]]));
    } else {
      setSelectedFiles(new Set());
    }
    setSelectedFiles(new Set(selectableIndices));
  };

  const deselectAllFiles = () => {
    setSelectedFiles(new Set());
  };


  const handleFileChange = async (event) => {
    const uploadedFiles = Array.from(event.target.files);
    
    // Check if files are from a folder (they will have a path structure)
    const isFromFolder = uploadedFiles.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));
    
    // Set file count limit for folder uploads (you can change this number)
    // const MAX_FILES_IN_FOLDER = 3;
    
    // // Validate file count for folder uploads
    // if (isFromFolder && uploadedFiles.length > MAX_FILES_IN_FOLDER) {
    //   showSnackbar(`Folder contains ${uploadedFiles.length} files. Maximum ${MAX_FILES_IN_FOLDER} files allowed per folder.`, 'error');
    //   event.target.value = null;
    //   return;
    // }
    
    // Validate file sizes
    const invalidFiles = uploadedFiles.filter(file => {
      const fileSizeInMB = file.size / (1024 * 1024);
      return fileSizeInMB > 20;
    });

    if (invalidFiles.length > 0) {
      showSnackbar(`${invalidFiles.length} file(s) exceed 20MB limit. Please select smaller files.`, 'error');
      event.target.value = null;
      return;
    }

    // Validate file types - only PDF and DOCX allowed for single file upload
    // For folder uploads, .xlsx and .csv are allowed but shown as non-selectable
    const validExtensions = isFromFolder ? ['.pdf','.docx','.xlsx','.csv'] : ['.pdf','.docx'];
    const invalidTypeFiles = uploadedFiles.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return !validExtensions.includes(extension);
    });

    if (invalidTypeFiles.length > 0) {
      if (isFromFolder) {
        showSnackbar(`${invalidTypeFiles.length} file(s) have invalid format. Only PDF, DOCX, XLSX, CSV files are allowed.`, 'error');
      } else {
        showSnackbar(`${invalidTypeFiles.length} file(s) have invalid format. Only PDF and DOCX files are allowed.`, 'error');
      }
      event.target.value = null;
      return;
    }
    
    // For single file upload (not from folder), reject .xlsx and .csv files
    if (!isFromFolder) {
      const excelOrCsvFiles = uploadedFiles.filter(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return extension === '.xlsx' || extension === '.csv';
      });
      if (excelOrCsvFiles.length > 0) {
        showSnackbar('XLSX and CSV files cannot be uploaded as single files. Please use folder upload instead.', 'error');
        event.target.value = null;
        return;
      }
    }

    if (uploadedFiles.length > 0) {
      setFiles(uploadedFiles);
      setFile(uploadedFiles[0]); // Keep first file for backward compatibility
      
      // Only reset selected files for folder uploads, not for single file uploads
      if (isFromFolder) {
        // Filter out .xlsx and .csv files from initial selection
        const selectableIndices = uploadedFiles
          .map((file, index) => {
            const extension = '.' + file.name.split('.').pop().toLowerCase();
            return (extension !== '.xlsx' && extension !== '.csv') ? index : null;
          })
          .filter(index => index !== null);
          const toSelect = selectableIndices.slice(0, 3);
          setSelectedFiles(new Set(toSelect));
      
          if (selectableIndices.length > 3) {
            showSnackbar(
              `Folder has ${selectableIndices.length} valid files. Only the first 3 are selected. You can deselect others from the list.`,
              'info'
            );
          }
      } else {
        // For single file uploads, check if file is selectable
        const extension = '.' + uploadedFiles[0].name.split('.').pop().toLowerCase();
        if (extension !== '.xlsx' && extension !== '.csv') {
          setSelectedFiles(new Set([0]));
        } else {
          setSelectedFiles(new Set());
        }
      }
      
      // Show appropriate message based on selection type
      if (isFromFolder) {
        showSnackbar(`Selected folder with ${uploadedFiles.length} files`, 'info');
      } else if (uploadedFiles.length === 1) {
        showSnackbar(`Selected file: ${uploadedFiles[0].name}`, 'info');
      } else {
        showSnackbar(`Selected ${uploadedFiles.length} files`, 'info');
      }
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleView = async () => {
    if (files.length > 0) {
      if (selectedFiles.size === 0) {
        showSnackbar('Please select at least one file to upload.', 'error');
        return;
      }
      
      // Filter out .xlsx and .csv files from selected files (safety check)
      const validSelectedFiles = Array.from(selectedFiles).filter(index => {
        const file = files[index];
        if (!file) return false;
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        return extension !== '.xlsx' && extension !== '.csv';
      });
      
      if (validSelectedFiles.length === 0) {
        showSnackbar('Please select at least one valid file to upload. XLSX and CSV files cannot be uploaded.', 'error');
        return;
      }
      
      setUploading(true);
      setLoading(true);
      setProgress(0);
      setIsCloseDisabled(true);
      setCurrentFileIndex(0);
      
      // Check if this is a single file upload (not from folder)
      const isFromFolder = files.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));
      
      if (!isFromFolder && files.length === 1) {
        // Single file upload - use the folder-style payload with one file
        // But first check if it's a valid file type
        const extension = '.' + files[0].name.split('.').pop().toLowerCase();
        if (extension === '.xlsx' || extension === '.csv') {
          showSnackbar('XLSX and CSV files cannot be uploaded. Please select a PDF or DOCX file.', 'error');
          setUploading(false);
          setLoading(false);
          setIsCloseDisabled(false);
          return;
        }
        simulateFileUpload();
        await uploadFile(files[0]);
      } else {
        // Multiple files or folder upload - use uploadMultipleFiles function
        simulateFileUpload();
        await uploadMultipleFiles(files);
      }

      onClose();
    }
  };

  // Check if current upload is from a folder
  const isFolderUpload = files.some(file => file.webkitRelativePath && file.webkitRelativePath.includes('/'));

  return (
    <Modal 
      open={open} 
      onClose={(loading || uploading) ? undefined : onClose} 
      aria-labelledby="modal-title" 
      aria-describedby="modal-description"
    >
      <Box sx={getModalStyle(isFolderUpload)} className='fileuploadModal'>
        <GlobalStyles styles={`@media (min-width: 1024px){ .fileuploadText { font-size: 12.8px !important; } }`} />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
            }}
          >
            <CircularProgress className=' text-primary' />
          </Box>
        )}

        <IconButton
          aria-label="close"
          onClick={resetState}
          disabled={isCloseDisabled}
          sx={{
            position: 'absolute',
            right: 8,
            top: 5,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" component="h2" textAlign="left" className='fw-bold fs-6 pb-1 addRfpText' sx={{ marginTop: '-13px' }}>
          Add RFP Files or Folders
        </Typography>

        <Divider />

        <Typography variant="h5" component="h2" textAlign="center" className='fw-500 mt-2 fs-7 addRfpText'>
          Upload RFP Files or Folders
        </Typography>

        <Box sx={{ flex: 1, overflowY: 'auto', width: '100%', mt: 1, px: { xs: 0, md: 0 } }}>

        <Box sx={{
          backgroundColor: 'rgba(215, 233, 245)',
          border: '1px dashed blue',
          borderRadius: '5px',
          textAlign: 'center',
          p: 5,
          mb: 0.5,
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          mx: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}
          className='fileuploadBox docuploadBox'
          onDragOver={(e) => {
            if (loading || uploading) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          onDragEnter={(e) => {
            if (loading || uploading) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          onDrop={(e) => {
            if (loading || uploading) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          <Box 
            onClick={() => {
              if (!loading && !uploading) {
                document.getElementById('raised-button-file').click();
              }
            }}
            sx={{
              cursor: (loading || uploading) ? 'not-allowed' : 'pointer',
              opacity: (loading || uploading) ? 0.5 : 1,
              pointerEvents: (loading || uploading) ? 'none' : 'auto'
            }}
          >
            <img 
              src={PDFICON} 
              alt='PDFICON' 
              width={'50px'} 
              height={'60px'} 
              style={{ cursor: (loading || uploading) ? 'not-allowed' : 'pointer' }} 
              className='pb-2 addRfpFile' 
            />
          </Box>

          <Typography className='fileuploadText' style={{ 
            textAlign: "center", 
            fontSize: '14px',
            width: '100%',
            maxWidth: '100%',
            wordWrap: 'break-word'
          }}>
            {!file && (
              <>
                Drag and Drop your Files here, or{' '}
                <label 
                  htmlFor="raised-button-file" 
                  className='fileuploadTextsub' 
                  style={{ 
                    color: (loading || uploading) ? '#999' : '#007bff', 
                    cursor: (loading || uploading) ? 'not-allowed' : 'pointer', 
                    fontSize: '14px',
                    pointerEvents: (loading || uploading) ? 'none' : 'auto',
                    opacity: (loading || uploading) ? 0.5 : 1
                  }}
                >
                  Browse Files
                </label>
                {' '}or{' '}
                <label 
                  htmlFor="raised-button-folder" 
                  className='fileuploadTextsub' 
                  style={{ 
                    color: (loading || uploading) ? '#999' : '#007bff', 
                    cursor: (loading || uploading) ? 'not-allowed' : 'pointer', 
                    fontSize: '14px',
                    pointerEvents: (loading || uploading) ? 'none' : 'auto',
                    opacity: (loading || uploading) ? 0.5 : 1
                  }}
                >
                  Browse Folder
                </label>
              </>
            )}
            {file && files.length === 1 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                mb: 1,
                width: '100%',
                maxWidth: 'calc(100% - 32px)', // Account for padding
                minWidth: 0,
                boxSizing: 'border-box'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666', 
                    fontSize: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    width: '100%'
                  }}
                  title={file.name}
                >
                  {file.name}
                </Typography>
              </Box>
            )}
            {files.length > 1 && (
              <Box sx={{ 
                maxHeight: '400px', 
                display: 'flex', 
                flexDirection: 'column',
                mb: 2,
                width: '100%',
                maxWidth: 'calc(100% - 32px)', // Account for padding
                boxSizing: 'border-box',
                textAlign: 'left'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, flexShrink: 0, textAlign: 'left' }}>
                  Selected {files.length} files ({selectedFiles.size}/3 selected):
                </Typography>
                <Box sx={{ 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  maxHeight: '350px',
                  fontFamily: 'Arial, sans-serif',
                  textAlign: 'left'
                }}>
                  {(() => {
                    // Group files by folder structure
                    const folderStructure = {};
                    files.forEach((file, index) => {
                      const pathParts = file.webkitRelativePath ? file.webkitRelativePath.split('/') : [file.name];
                      let current = folderStructure;
                      
                      for (let i = 0; i < pathParts.length - 1; i++) {
                        const folderName = pathParts[i];
                        if (!current[folderName]) {
                          current[folderName] = { type: 'folder', children: {}, files: [] };
                        }
                        current = current[folderName].children;
                      }
                      
                      const fileName = pathParts[pathParts.length - 1];
                      current[fileName] = { type: 'file', file, index };
                    });

                    const renderTree = (structure, level = 0, parentPath = '') => {
                      const items = [];
                      
                      Object.entries(structure).forEach(([name, item], idx, arr) => {
                        const isLast = idx === arr.length - 1;
                        const indent = '│  '.repeat(level);
                        const connector = isLast ? '└── ' : '├── ';
                        const fullPath = parentPath ? `${parentPath}/${name}` : name;
                        
                        if (item.type === 'folder') {
                          // Get only direct file indices in this specific folder
                          const getDirectFileIndices = (currentFolderStructure) => {
                            const indices = [];
                            Object.values(currentFolderStructure).forEach(child => {
                              if (child.type === 'file') {
                                indices.push(child.index);
                              }
                            });
                            return indices;
                          };
                          
                          const folderFileIndices = getDirectFileIndices(item.children);
                          const allFolderFilesSelected = folderFileIndices.length > 0 && folderFileIndices.every(index => selectedFiles.has(index));
                          const someFolderFilesSelected = folderFileIndices.some(index => selectedFiles.has(index));
                          
                          const handleFolderCheckboxChange = () => {
                            // Disable folder selection during upload
                            if (loading || uploading) {
                              return;
                            }
                            if (allFolderFilesSelected) {
                              // Deselect only direct files in this specific folder
                              const newSelectedFiles = new Set(selectedFiles);
                              folderFileIndices.forEach(index => newSelectedFiles.delete(index));
                              setSelectedFiles(newSelectedFiles);
                            } else {
                              // Select only direct files in this specific folder
                              const newSelectedFiles = new Set(selectedFiles);
                              folderFileIndices.forEach(index => newSelectedFiles.add(index));
                              setSelectedFiles(newSelectedFiles);
                            }
                          };

                          // Folder
                          items.push(
                            <Box key={`folder-${name}`} sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 0.5,
                              fontFamily: 'Arial, sans-serif',
                              fontSize: '12px',
                              overflow: 'hidden'
                            }}>
                              <Typography sx={{ 
                                color: '#2196f3',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '12px',
                                whiteSpace: 'pre',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'left'
                              }}
                              title={`${indent}${connector}📁 ${name}`}
                              >
                                {indent}{connector}📁 {name}
                </Typography>
                            </Box>
                          );
                          
                          // Render folder contents
                          items.push(...renderTree(item.children, level + 1, fullPath));
                        } else {
                          // File
                          const fileSize = (item.file.size / (1024 * 1024)).toFixed(2);
                          const extension = '.' + item.file.name.split('.').pop().toLowerCase();
                          const isNonSelectable = extension === '.xlsx' || extension === '.csv';
                          
                          items.push(
                            <Box 
                              key={`file-${name}`} 
                              onClick={isNonSelectable ? undefined : () => toggleFileSelection(item.index)}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 0.5,
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '12px',
                                cursor: (loading || uploading) ? 'not-allowed' : (isNonSelectable ? 'default' : 'pointer'),
                                opacity: (loading || uploading) ? 0.5 : 1,
                                pointerEvents: (loading || uploading || isNonSelectable) ? 'none' : 'auto',
                                '&:hover': { 
                                  backgroundColor: (loading || uploading || isNonSelectable) ? 'transparent' : '#f0f0f0' 
                                }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden'
                              }}>
                                <Typography sx={{ 
                                  color: '#333',
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '12px',
                                  whiteSpace: 'pre',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: 'left'
                                }}
                                title={`${indent}${connector}📄 ${name}`}
                                >
                                  {indent}{connector}📄 {name}
                                </Typography>
                              </Box>
                              {selectedFiles.has(item.index) && !isNonSelectable && (
                                <Box sx={{ 
                                  color: '#4caf50',
                                  padding: '2px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <CheckIcon fontSize="small" />
                                </Box>
                              )}
                            </Box>
                          );
                        }
                      });
                      
                      return items;
                    };

                    return renderTree(folderStructure);
                  })()}
                </Box>
              </Box>
            )}
            <Typography className='subtextfu' sx={{ color: '#888', fontSize: '10px', textAlign: 'center' }}>
              Limit 50MB per file PDF, DOCX, XLSX, CSV Files Only
            </Typography>
          </Typography>

          <input
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={loading || uploading}
          />
          <input
            accept=".pdf,.docx,.xlsx,.csv"
            style={{ display: 'none' }}
            id="raised-button-folder"
            type="file"
            multiple
            webkitdirectory=""
            onChange={handleFileChange}
            disabled={loading || uploading}
          />
        </Box>
        {!file && (
          <Typography className='fileuploadText' style={{ textAlign: 'center', marginTop: '4px', marginBottom: '8px' }}>
            <span style={{ color: '#0d6efd', fontWeight: 600, marginRight: '4px' }}>Note:</span>
            <span style={{ color: 'red', fontSize: '12px' }}>Please select only three files while uploading the folder.</span>
          </Typography>
        )}
        {!file && (
          <Box textAlign="center" mt={0} mb={1} sx={{ width: '92%', marginLeft: '18px' }}>
            <Divider>
              <Typography align="center" sx={{ color: '#888' }}>
                Or
              </Typography>
            </Divider>
            <Button 
              variant="contained" 
              onClick={() => navigate('/AddNewRfp')} 
              className='bg-primary mt-2 w-50 rounded-2 text-capitalize RfpDetailsButton'
              disabled={loading || uploading}
            >
              Enter RFP Details
            </Button>
          </Box>
        )}

        {uploading && (
          <Box sx={{ 
            width: '100%', 
            position: 'sticky',
            bottom: '60px',
            zIndex: 10,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            padding: '8px 0'
          }} className='fileuploadBox'>
            <div className='border border-1px solid grey p-2'>
              <Box alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <img src={PDFICON} alt='PDFICON' width={'30px'} height={'42px'} style={{ cursor: 'pointer' }} className=' mx-1' />
                  <Box ml={2}>
                    <div style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography className='pdfnamefu' style={{ textDecoration: 'none', width: "200px", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                        {files[currentFileIndex]?.name}
                      </Typography>
                      {files.length > 1 && (
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                          File {currentFileIndex + 1} of {files.length}
                        </Typography>
                      )}
                    </div>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: '100%', marginTop: '15px' }} className='progressbar'>
                <LinearProgress variant="determinate" value={progress} sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4caf50',
                  },
                }} />
                {files.length > 1 && (
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '10px', mt: 0.5, display: 'block' }}>
                    Overall Progress: {Math.round(progress)}%
                  </Typography>
                )}
              </Box>
            </div>
          </Box>
        )}

        </Box> {/* end scrollable content */}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'end', 
          mt: uploading ? 0 : 0.5,
          position: files.length > 0 ? 'relative' : 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          paddingTop: '8px',
          paddingBottom: files.length > 0 ? '0px' : '8px',
          borderTop: uploading ? 'none' : '1px solid #e0e0e0',
          zIndex: 11
        }}>
          {files.length > 0 && (
            <Button 
              color="primary" 
              onClick={handleView} 
              disabled={loading || selectedFiles.size === 0 || selectedFiles.size > 3} 
              className='rounded-2 bg-primary text-white viewButton'
            >
              {uploading ? 'Extract': `Submit ${selectedFiles.size > 0 ? `(${selectedFiles.size} selected)` : `(${files.length} files)`}`}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const getModalStyle = (isFolderUpload) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  pt: 5,
  px: 5,
  pb: 1,
  width: '98%',
  maxWidth: '1200px',
  height: '98%',
  maxHeight: '900px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  ...(isFolderUpload && {
    '@media (min-width: 1024px)': {
      width: '692px !important',
      height: 'auto !important',
      maxHeight: '555px !important'
    }
  })
});

export default FileUploadModal;